import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import {
  Avatar,
  Badge,
  Box,
  Button,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import BellIcon from "../../icons/Bell";
import ArrowRightIcon from "../../icons/ArrowRight";
import ChatAltIcon from "../../icons/ChatAlt";
import { NotificationsApi } from "../../__fakeApi__/NotificationsApi";
import useAuth from "../../hooks/useAuth";
import ScrollBar from "react-perfect-scrollbar";
import { useTranslation } from "react-i18next";

const NotificationsPopover = () => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(0);
  const limit = 10;

  const notificationCount = notifications.filter(n => !n.is_read).length;
  const totalPages = Math.ceil(notifications.length / limit);
  const paginated = notifications.slice(page * limit, page * limit + limit);

  // ---------------------------
  // Fetch Notifications
  // ---------------------------
  const fetchNotifications = async () => {
    const data = await NotificationsApi.get_notifications(user.id, true);
    setNotifications(Array.isArray(data) ? data : []);
    setPage(0); // reset pagination when opening
  };

  // Load notifications only when popover opens
  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open]);

  // ---------------------------
  // Handlers
  // ---------------------------
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handlePageChange = (_, value) => {
    setPage(value - 1);
  };

  const mark_as_read = async (id) => {
    await NotificationsApi.mark_as_read(user.id, [id]);
  };

  const mark_all_as_read = async () => {
    await NotificationsApi.mark_all_as_read(user.id,true);
    setOpen(false);
  };
    const formatDate = (value) => {
      try {
        return new Date(value).toLocaleString();
      } catch {
        return "";
      }
    };

  return (
    <>
      <Tooltip title={t("System Notifications")}>
        <IconButton color="inherit" ref={anchorRef} onClick={handleOpen}>
          <Badge color="error" badgeContent={notificationCount}>
            <BellIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        onClose={handleClose}
        open={open}
        PaperProps={{ sx: { width: 500, maxHeight: 600 } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography color="textPrimary" variant="h6">
            {t("System Notifications")}
          </Typography>
        </Box>

        {notifications.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography color="textPrimary" variant="subtitle2">
              {t("There are no system notifications")}
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
              <Pagination count={totalPages} size="small" onChange={handlePageChange} />
            </Box>

            <ScrollBar>
              <List disablePadding sx={{ height: 400 }}>
                {paginated.map((n) => (
                  <ListItem divider key={n.id}>
                    <ListItemAvatar>
                      <Avatar sx={{ backgroundColor: "primary.main", color: "primary.contrastText" }}>
                        <ChatAltIcon fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
<ListItemText
  primary={
    <Link
      color="textPrimary"
      sx={{ cursor: "pointer" }}
      underline="none"
      variant="subtitle2"
    >
      {n.subject}
    </Link>
  }
  secondary={
    <>
      {n.content}
      <Typography
        variant="caption"
        display="block"
        color="textSecondary"
        sx={{ mt: 0.5 }}
      >
        {formatDate(n.created_on)}
      </Typography>
    </>
  }
/>


                    <IconButton
                      edge="end"
                      sx={{ backgroundColor: "primary.main", color: "primary.contrastText" }}
                      onClick={async () => {
                        await mark_as_read(n.id);
                        navigate(n.url || "", {
                          state: { id: n.realated_entity_id },
                        });
                        handleClose();
                      }}
                    >
                      <ArrowRightIcon fontSize="small" />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            </ScrollBar>

            <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
              <Button color="primary" size="small" variant="text" onClick={mark_all_as_read}>
                {t("Mark all as read")}
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
};

export default NotificationsPopover;
